import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { M } from '@dashboard-experience/mastodon';
import { i18n } from '@international/mastodon-i18n';
import './CandidateLandingPage.scss';
import { ReportSummary } from 'api/candidates/types/Report';
import {
  CheckrPersonalProfileBanner,
  LegacyPersonalProfileBanner,
} from 'components/CheckrPersonalProfileBanner';
import Faq from 'components/Faq';
import ReviewCTABannerContainer from 'components/ReviewCTABanner';
import ReportList from './ReportList';
import { trackAnalyticsEvent } from '../../../actions';
import { setCurrentReportId } from '../../../api/report/currentReport';
import { ANALYTICS_EVENTS } from '../../../lib/analytics';
import I18nLink from '../../I18nLink/I18nLink';
import { useChat } from './useChat';

export interface CandidateLandingPageProps {
  firstName?: string;
  reports: ReportSummary[];
}

const CandidateLandingPage: React.FC<CandidateLandingPageProps> = ({
  firstName,
  reports = [],
}) => {
  const orderedReports: ReportSummary[] = reports.map((report, i) => ({
    ...report,
    reportOrder: i + 1,
  }));
  useChat(globalThis?.location?.href || '', reports.length);
  const dispatch = useDispatch();
  const applicantId = useSelector((state: any) => state.portal.applicantId);
  // The landing page's events are tagged with the first report, as they were
  // when this was `currentReportIndex: 0`. Declared first so it runs before the
  // view event below.
  const firstReportId = reports[0]?.id;
  useEffect(() => {
    setCurrentReportId(firstReportId);
  }, [firstReportId]);
  useEffect(() => {
    dispatch(trackAnalyticsEvent(ANALYTICS_EVENTS.LANDING_PAGE_VIEWED));
  }, [dispatch]);

  let subtitleKey = 'components.CandidateLandingPage.manageYourReportsMessage';
  let faqKey = 'components.CandidateLandingPage.Faq';
  if (reports.length === 0) {
    subtitleKey = applicantId
      ? 'components.CandidateLandingPage.subtitleExpired'
      : 'components.CandidateLandingPage.subtitleNoRecord';
    faqKey = applicantId
      ? 'components.CandidateLandingPage.FaqExpiredReports'
      : 'components.CandidateLandingPage.FaqNonCandidate';
  }

  return (
    <div className='candidate-landing-page'>
      <M.Grid fullWidth as='header' className='candidate-header'>
        <ReviewCTABannerContainer />
        <LegacyPersonalProfileBanner location='home' />
        <M.Container type='transparent' className='candidate-header-container'>
          <M.Container.Row>
            <M.Container.Col>
              <h2 id='greeting'>
                {firstName
                  ? i18n.getStr(
                      'components.CandidateLandingPage.greeting',
                      firstName,
                    )
                  : i18n.getStr('components.CandidateLandingPage.welcome')}
              </h2>
              <p id='manageYourReportsMessage'>{i18n.getStr(subtitleKey)}</p>
            </M.Container.Col>
          </M.Container.Row>
        </M.Container>
      </M.Grid>
      <M.Grid fullWidth className='candidate-body'>
        <M.Container as='main' type='transparent' className='candidate-main'>
          <CheckrPersonalProfileBanner />
          <ReportList reports={orderedReports} />
        </M.Container>
        <M.Container
          type='transparent'
          as='footer'
          className='candidate-footer'
        >
          <M.GridCol className='candidate-footer-container'>
            <Faq translationKey={faqKey} />
            <I18nLink
              translationKey={[
                `translationKey.helpCenterMessage`,
                'components.Faq.helpCenterMessage',
              ]}
              className='faq-help-center-message'
              id='faq-help-center-message'
              onLinkClick={() =>
                dispatch(
                  trackAnalyticsEvent(ANALYTICS_EVENTS.HELP_CENTER_VIEWED),
                )
              }
            />
          </M.GridCol>
        </M.Container>
      </M.Grid>
    </div>
  );
};

export default i18n.renderTranslation()(
  CandidateLandingPage,
) as React.FC<CandidateLandingPageProps>;
